// App.tsx
import { BrowserRouter, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Button } from "@nexus/ui";
import { LoaderCircle, RefreshCw } from "lucide-react";
import { AuthProvider } from "react-oidc-context";
import { WidgetAssetsContext } from "@nexus/widget-sdk";
import { publicConfigQueryOptions } from "@/auth/oidcConfig";
import { rememberSession, returnToFromState } from "@/auth/session";
import { SessionKeeper } from "@/auth/SessionKeeper";
import { SessionQueryProvider } from "@/auth/SessionQueryProvider";
import { PreviewRail } from "@/components/layout/PreviewRail";
import { AppProvider } from "@/contexts/AppContext";
import { AccountSettingsProvider } from "@/contexts/AccountSettingsContext";
import { AuthorizationProvider } from "@/contexts/AuthorizationContext";
import { PublicConfigContext } from "@/contexts/PublicConfigContext";
import { SettingsModalProvider } from "@/contexts/SettingsModalContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { SidePanelProvider } from "@/contexts/SidePanelContext";
import {
  getWorkspaceLandingPath,
  useWorkspaceAudience,
  WorkspaceAudienceProvider,
} from "@/contexts/WorkspaceAudienceContext";
import { useTheme } from "@/hooks/useTheme";
import { resolveDemoAsset } from "@/lib/demoAssets";
import { AppRoutes } from "@/routes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WidgetAssetsContext.Provider value={resolveDemoAsset}>
        <BrowserRouter>
          <WorkspaceAudienceProvider>
            <AuthRoot />
          </WorkspaceAudienceProvider>
        </BrowserRouter>
      </WidgetAssetsContext.Provider>
    </QueryClientProvider>
  );
}

function AuthRoot() {
  const navigate = useNavigate();
  const { audience } = useWorkspaceAudience();
  const discovery = useQuery(publicConfigQueryOptions);

  if (!discovery.data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-6 text-primary">
        <div className="w-full max-w-sm space-y-4">
          {discovery.isError && !discovery.isFetching ? (
            <>
              <h1 className="text-lg font-semibold">Unable to connect</h1>
              <p role="alert" className="text-sm text-secondary">
                Authentication is unavailable. Please try again.
              </p>
              <Button onClick={() => void discovery.refetch()} disabled={discovery.isFetching}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Retry
              </Button>
            </>
          ) : (
            <p
              role="status"
              className="flex items-center justify-center gap-2 text-sm text-secondary"
            >
              <LoaderCircle
                className="h-4 w-4 animate-spin text-brand-cyan motion-reduce:animate-none"
                aria-hidden="true"
              />
              Connecting...
            </p>
          )}
        </div>
      </main>
    );
  }

  return (
    <PublicConfigContext.Provider value={{ supportEmail: discovery.data.supportEmail }}>
      <AuthProvider
        key={JSON.stringify(discovery.data.oidc)}
        {...discovery.data.oidc}
        onSigninCallback={(user) => {
          rememberSession();
          navigate(returnToFromState(user?.state) ?? getWorkspaceLandingPath(audience), {
            replace: true,
          });
        }}
      >
        <SessionQueryProvider>
          <SessionKeeper />
          <PreviewRail />
          <AuthorizationProvider>
            <AccountSettingsProvider>
              <AppProvider>
                <ThemeController />
                <SettingsModalProvider>
                  <SidebarProvider>
                    <SidePanelProvider>
                      <AppRoutes />
                    </SidePanelProvider>
                  </SidebarProvider>
                </SettingsModalProvider>
              </AppProvider>
            </AccountSettingsProvider>
          </AuthorizationProvider>
        </SessionQueryProvider>
      </AuthProvider>
    </PublicConfigContext.Provider>
  );
}

function ThemeController() {
  useTheme();
  return null;
}
